import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Package, ArrowLeft, Plus, ArrowsLeftRight, PencilSimple, Tag, ArrowCircleDown, WarningCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ItemInstructions } from './ItemInstructions';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditMfg } from '@/auth/AuthContext';
import { mfgApi, TYPE_BADGE } from './mfgApi';
import { useDepartment } from './DepartmentContext';
import LabelPrintDialog from '@/components/labels/LabelPrintDialog';
import { buildItemLabel, buildLotLabel } from '@/components/labels/labelSizes';
import BarcodeLabel from './BarcodeLabel';
import AttachmentsPanel from './AttachmentsPanel';
import IssueStockDialog from './IssueStockDialog';
import LinkedDocsPanel from '@/pages/docs/LinkedDocsPanel';
import { formatDate } from '@/utils/datetime';
import { ItemLifecycleActions } from '@/components/lifecycle/ItemLifecycleActions';

const NONE = '__none__';
const fmtDate = (s) => (s ? formatDate(s) : '—');

export default function ItemDetailDialog({ open, onClose, itemId, onChanged }) {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditMfg(user);
  const [item, setItem] = useState(null);
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [op, setOp] = useState(null); // null | { mode, lot? }
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [labelData, setLabelData] = useState(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [linked, setLinked] = useState(null);
  const { active } = useDepartment();
  const deptName = active?.name;

  const load = useCallback(() => { if (itemId) mfgApi.item(itemId).then(setItem).catch(() => {}); }, [itemId]);
  useEffect(() => {
    if (open) {
      load();
      mfgApi.locations().then(setLocations).catch(() => {});
      mfgApi.suppliers().then(setSuppliers).catch(() => {});
      if (itemId) mfgApi.linkedAssets(itemId).then(setLinked).catch(() => setLinked(null));
      setOp(null);
    }
  }, [open, load, itemId]);

  const openReceive = () => { setF({ quantity: '', lot_no: '', location_id: item?.default_location_id || '', expiry_date: '', supplier_id: item?.supplier_id || '', unit_cost: item?.cost ?? '', notes: '' }); setOp({ mode: 'receive' }); };
  const openAdjust = (lot) => { setF({ new_quantity: lot.quantity, reason: '' }); setOp({ mode: 'adjust', lot }); };
  const openTransfer = (lot) => { setF({ to_location_id: '', quantity: '', reason: '' }); setOp({ mode: 'transfer', lot }); };

  const submit = async () => {
    setBusy(true);
    try {
      if (op.mode === 'receive') {
        if (!Number(f.quantity)) { throw { response: { data: { detail: t('mfg.lot.qty') } } }; }
        await mfgApi.receive({ item_id: itemId, quantity: Number(f.quantity), lot_no: f.lot_no || null, location_id: f.location_id || null, expiry_date: f.expiry_date || null, supplier_id: f.supplier_id || null, unit_cost: f.unit_cost === '' ? null : Number(f.unit_cost), notes: f.notes || null });
        toast.success(t('mfg.receive.done'));
      } else if (op.mode === 'adjust') {
        await mfgApi.adjustLot(op.lot.id, { new_quantity: Number(f.new_quantity) || 0, reason: f.reason || null });
        toast.success(t('mfg.adjust.done'));
      } else if (op.mode === 'transfer') {
        await mfgApi.transferLot(op.lot.id, { to_location_id: f.to_location_id || null, quantity: Number(f.quantity) || 0, reason: f.reason || null });
        toast.success(t('mfg.transfer.done'));
      }
      setOp(null); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
    finally { setBusy(false); }
  };

  const locCode = (id) => locations.find((l) => l.id === id)?.code || '—';
  const opTitleKey = op ? (op.mode === 'receive' ? 'mfg.receive.title' : op.mode === 'adjust' ? 'mfg.adjust.title' : 'mfg.transfer.title') : '';

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="item-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Package size={20} className="text-orange-500" weight="duotone" /> {item?.name || '…'}
            {item && <Badge variant="outline" className={TYPE_BADGE[item.type]}>{t(`mfg.type.${item.type}`)}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {!item ? null : op ? (
          <div className="space-y-3" data-testid="item-op-form">
            <button onClick={() => setOp(null)} className="inline-flex items-center gap-1 text-sm text-zinc-500"><ArrowLeft size={15} /> {t('mfg.item.lots')}</button>
            <h3 className="font-semibold text-zinc-800">{t(opTitleKey)}{op.lot ? ` — ${op.lot.lot_no}` : ''}</h3>
            {op.mode === 'receive' && (
              <>
                {item.pack_size > 0 && (
                  <div className="rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="receive-pack-helper">
                    <span className="text-xs text-zinc-600">1{item.pack_uom || '袋'} = {item.pack_size}{item.uom}</span>
                    <Input type="number" min="0" step="1" placeholder={item.pack_uom || '袋'} className="h-8 w-20"
                      data-testid="receive-pack-count"
                      onChange={(e) => {
                        const n = Number(e.target.value) || 0;
                        setF((p) => ({ ...p, quantity: n > 0 ? String(n * item.pack_size) : '' }));
                      }} />
                    <span className="text-xs text-zinc-500">× {item.pack_size}{item.uom} → 数量に反映</span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.lot.qty')} ({item.uom}) *</Label><Input type="number" value={f.quantity} onChange={(e) => setF((p) => ({ ...p, quantity: e.target.value }))} className="h-10 mt-1" data-testid="receive-qty-input" /></div>
                  <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.lot.location')}</Label>
                    <Select value={f.location_id || NONE} onValueChange={(v) => setF((p) => ({ ...p, location_id: v === NONE ? '' : v }))}><SelectTrigger className="h-10 mt-1" data-testid="receive-location-select"><SelectValue placeholder={t('mfg.none')} /></SelectTrigger><SelectContent><SelectItem value={NONE}>{t('mfg.none')}</SelectItem>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.picker_label || l.code}</SelectItem>)}</SelectContent></Select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {/* A3: ロット管理が OFF の品目ではロット番号を聞かない（自動発番する） */}
                  {item.lot_tracked && (
                    <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.lot.no')} <span className="text-zinc-400 font-normal">({t('mfg.receive.lotAuto')})</span></Label><Input value={f.lot_no} onChange={(e) => setF((p) => ({ ...p, lot_no: e.target.value }))} className="h-10 mt-1 font-mono" data-testid="receive-lot-input" /></div>
                  )}
                  <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.lot.expiry')}</Label><DateField value={f.expiry_date || ''} onChange={(v) => setF((p) => ({ ...p, expiry_date: v }))} className="mt-1" inputClassName="h-10" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.item.cost')}</Label><Input type="number" value={f.unit_cost} onChange={(e) => setF((p) => ({ ...p, unit_cost: e.target.value }))} className="h-10 mt-1" /></div>
                  <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.item.supplier')}</Label>
                    <Select value={f.supplier_id || NONE} onValueChange={(v) => setF((p) => ({ ...p, supplier_id: v === NONE ? '' : v }))}><SelectTrigger className="h-10 mt-1"><SelectValue placeholder={t('mfg.none')} /></SelectTrigger><SelectContent><SelectItem value={NONE}>{t('mfg.none')}</SelectItem>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
                </div>
              </>
            )}
            {op.mode === 'adjust' && (
              <div className="space-y-3">
                <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.adjust.newQty')} ({item.uom})</Label><Input type="number" value={f.new_quantity} onChange={(e) => setF((p) => ({ ...p, new_quantity: e.target.value }))} className="h-10 mt-1" data-testid="adjust-qty-input" /></div>
                <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.adjust.reason')}</Label><Input value={f.reason} onChange={(e) => setF((p) => ({ ...p, reason: e.target.value }))} className="h-10 mt-1" /></div>
              </div>
            )}
            {op.mode === 'transfer' && (
              <div className="space-y-3">
                <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.transfer.toLocation')}</Label>
                  <Select value={f.to_location_id || NONE} onValueChange={(v) => setF((p) => ({ ...p, to_location_id: v === NONE ? '' : v }))}><SelectTrigger className="h-10 mt-1" data-testid="transfer-location-select"><SelectValue placeholder={t('mfg.none')} /></SelectTrigger><SelectContent><SelectItem value={NONE}>{t('mfg.none')}</SelectItem>{locations.filter((l) => l.id !== op.lot.location_id).map((l) => <SelectItem key={l.id} value={l.id}>{l.picker_label || l.code}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.lot.qty')} ({item.uom}) — {t('mfg.item.onHand')}: {op.lot.quantity}</Label><Input type="number" value={f.quantity} onChange={(e) => setF((p) => ({ ...p, quantity: e.target.value }))} className="h-10 mt-1" data-testid="transfer-qty-input" /></div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setOp(null)}>{t('mfg.cancel')}</Button>
              <Button onClick={submit} disabled={busy} data-testid="op-submit-btn" className="bg-teal-700 hover:bg-teal-800">{busy ? t('mfg.saving') : t('mfg.save')}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* §8.5 ひとつのライフサイクル — 全台帳で同じ操作 */}
            <ItemLifecycleActions register="mfg" itemId={item.id}
              onChanged={() => { load(); onChanged?.(); }} testId="mfg-item-lifecycle" />
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div><p className="text-xs text-zinc-400">{t('mfg.item.sku')}</p><p className="font-mono font-medium">{item.sku}</p></div>
              <div><p className="text-xs text-zinc-400">{t('mfg.item.onHand')}</p><p className="font-bold text-lg" data-testid="detail-on-hand">{item.on_hand} <span className="text-xs font-normal text-zinc-400">{item.uom}</span></p></div>
              <div><p className="text-xs text-zinc-400">{t('mfg.item.category')}</p><p>{item.category || '—'}</p></div>
            </div>

            {/* A4 · 品質警告。保存はブロックしない（表示だけ） */}
            {item.needs_input && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-start gap-2" data-testid="item-needs-input-notice">
                <WarningCircle size={16} className="text-amber-600 mt-0.5 shrink-0" weight="fill" />
                <p className="text-xs text-amber-900">
                  <span className="font-semibold">{t('mfg.item.needsInput')}</span>
                  <span className="ml-1">{t('mfg.item.missingFields')}: {(item.missing_required || []).join('、')}</span>
                </p>
              </div>
            )}

            <BarcodeLabel value={item.barcode || item.sku} name={item.name} sku={item.sku}
              onPrint={() => setLabelData([buildItemLabel(item, deptName)])} />

            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-zinc-800">{t('mfg.item.lots')}</h3>
              <div className="flex items-center gap-2">
                {canWrite && <Button size="sm" variant="outline" onClick={() => setIssueOpen(true)} data-testid="item-issue-btn" className="h-9 border-rose-300 text-rose-700 hover:bg-rose-50"><ArrowCircleDown size={15} className="mr-1" weight="bold" /> {t('mfg.action.issue')}</Button>}
                {canWrite && <Button size="sm" onClick={openReceive} data-testid="item-receive-btn" className="h-9 bg-teal-700 hover:bg-teal-800"><Plus size={15} className="mr-1" weight="bold" /> {t('mfg.action.receive')}</Button>}
              </div>
            </div>
            <div className="rounded-lg border border-zinc-200 overflow-hidden">
              <table className="w-full text-sm" data-testid="item-lots-table">
                <thead><tr className="bg-zinc-100 text-zinc-700 text-xs border-b border-zinc-200">
                  <th className="text-left font-semibold px-3 py-2">{t('mfg.lot.no')}</th>
                  <th className="text-left font-semibold px-3 py-2">{t('mfg.lot.location')}</th>
                  <th className="text-right font-semibold px-3 py-2">{t('mfg.lot.qty')}</th>
                  <th className="text-left font-semibold px-3 py-2">{t('mfg.lot.expiry')}</th>
                  <th className="text-right font-semibold px-3 py-2"></th>
                </tr></thead>
                <tbody>
                  {(item.lots || []).filter((l) => l.quantity > 0).length === 0 ? (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-zinc-400" data-testid="item-lots-empty">{t('mfg.lot.noLots')}</td></tr>
                  ) : item.lots.filter((l) => l.quantity > 0).map((l) => (
                    <tr key={l.id} className="border-t border-zinc-200" data-testid={`lot-row-${l.id}`}>
                      <td className="px-3 py-2 font-mono text-zinc-700">{l.lot_no}</td>
                      <td className="px-3 py-2 font-mono text-zinc-600">{locCode(l.location_id)}</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums">{l.quantity}</td>
                      <td className="px-3 py-2 text-zinc-600">{fmtDate(l.expiry_date)}</td>
                      <td className="px-3 py-2"><div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setLabelData([buildLotLabel(item, l, locCode(l.location_id), deptName)])} data-testid={`lot-label-${l.id}`} className="h-7 px-2 text-zinc-600" title={t('mfg.label.printRow')}><Tag size={15} /></Button>
                        {canWrite && <Button size="sm" variant="ghost" onClick={() => openAdjust(l)} data-testid={`lot-adjust-${l.id}`} className="h-7 px-2 text-zinc-600" title={t('mfg.action.adjust')}><PencilSimple size={15} /></Button>}
                        {canWrite && <Button size="sm" variant="ghost" onClick={() => openTransfer(l)} data-testid={`lot-transfer-${l.id}`} className="h-7 px-2 text-zinc-600" title={t('mfg.action.transfer')}><ArrowsLeftRight size={15} /></Button>}
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {linked && ((linked.it_assets || []).length + (linked.eng_assets || []).length) > 0 && (
              <div data-testid="item-linked-assets">
                <h3 className="font-semibold text-zinc-800 flex items-center gap-1.5">
                  関連資産
                  <span className="text-[11px] font-normal text-zinc-400">この品目から登録された資産</span>
                </h3>
                <ul className="mt-2 rounded-lg border border-zinc-200 divide-y divide-zinc-200 overflow-hidden">
                  {(linked.it_assets || []).map((a) => (
                    <li key={a.id} data-testid={`linked-it-asset-${a.id}`}>
                      <a href={`/it/assets?open=${a.id}`} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-50">
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-violet-50 text-violet-700 border-violet-200 shrink-0">IT資産</span>
                        <span className="text-zinc-800 truncate">{a.name}</span>
                        {a.asset_tag && <span className="font-mono text-[11px] text-zinc-400 shrink-0">{a.asset_tag}</span>}
                        <span className="ml-auto text-[11px] text-zinc-400 shrink-0">{a.assigned_to_name || a.location || ''}</span>
                      </a>
                    </li>
                  ))}
                  {(linked.eng_assets || []).map((a) => (
                    <li key={a.id} data-testid={`linked-eng-asset-${a.id}`}>
                      <a href={`/engineering?open=${a.id}`} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-50">
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-teal-50 text-teal-700 border-teal-200 shrink-0">技術資産</span>
                        <span className="text-zinc-800 truncate">{a.name}</span>
                        {a.asset_no && <span className="font-mono text-[11px] text-zinc-400 shrink-0">{a.asset_no}</span>}
                        <span className="ml-auto text-[11px] text-zinc-400 shrink-0">{a.location || ''}{typeof a.qty_total === 'number' ? ` ・ ${a.qty_available ?? a.qty_total}/${a.qty_total}` : ''}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 2026-11-05 · 作業手順・検査（技術が直し、現場が見る） */}
            <div className="rounded-lg border border-zinc-200 p-3">
              <button className="flex w-full items-center justify-between text-left"
                onClick={() => setShowInstructions((v) => !v)} data-testid="item-tab-instructions">
                <h3 className="font-semibold text-zinc-800">作業手順・検査</h3>
                <span className="text-xs text-zinc-500">{showInstructions ? '閉じる' : '開く'}</span>
              </button>
              {showInstructions && <div className="mt-3"><ItemInstructions item={item} /></div>}
            </div>

            <AttachmentsPanel entityType="item" entityId={item.id} canWrite={canWrite}
              title={t('mfg.attach.itemTitle')} testid="item-detail-attachments" />

            <LinkedDocsPanel targetType="mfg_item" targetId={item.id}
              className="pt-2 border-t border-zinc-200" />
          </div>
        )}
      </DialogContent>
    </Dialog>
    <LabelPrintDialog open={!!labelData} onClose={() => setLabelData(null)} labels={labelData || []} title="品目・ロットラベル" />
    <IssueStockDialog open={issueOpen} onClose={() => setIssueOpen(false)} item={item}
      locations={locations} onDone={() => { load(); onChanged?.(); }} />
    </>
  );
}
